import React from 'react'
import { Document, Image, Page, Text, View } from '@react-pdf/renderer'
import moment from 'moment'
import AmbuNetLogo from '../../../assets/images/ambunetLogo.png'
import { REGISTERS } from './constants'

// Landscape A4 export of one hazard register: a cover page with the
// download stamp and headline numbers, the DCB0129 risk matrix as a colour
// key, a register table (one row per hazard), then a detail block per hazard
// with causes, impacts and controls. Colours match the CRM's chips so the
// PDF reads the same as the screen.

const RISK_COLOR = {
  Low: '#27803B',
  Medium: '#B54708',
  High: '#B42318',
  Critical: '#7A271A',
}

const STATUS_COLOR = {
  Open: '#B42318',
  'In Progress': '#B54708',
  Mitigated: '#27803B',
  Accepted: '#268EBB',
}

const ACCEPTABILITY_COLOR = {
  Acceptable: '#27803B',
  ALARP: '#B54708',
  Unacceptable: '#B42318',
}

const NAVY = '#123F4A'
const TEAL = '#239AA4'
const MUTED = '#64748B'
const LINE = '#E2E8F0'

const LIKELIHOODS = ['Almost Certain', 'Likely', 'Possible', 'Unlikely', 'Rare']
const SEVERITIES = ['Negligible', 'Minor', 'Moderate', 'Major', 'Catastrophic']
const MATRIX = {
  'Almost Certain': ['Medium', 'High', 'High', 'Critical', 'Critical'],
  Likely: ['Low', 'Medium', 'High', 'Critical', 'Critical'],
  Possible: ['Low', 'Medium', 'Medium', 'High', 'High'],
  Unlikely: ['Low', 'Low', 'Medium', 'Medium', 'High'],
  Rare: ['Low', 'Low', 'Low', 'Low', 'Medium'],
}

// Helvetica (the built-in PDF font) only covers Latin-1, so swap the symbols
// hazard text tends to contain for ASCII and drop anything else rather than
// print tofu.
const clean = (value) =>
  String(value ?? '')
    .replace(/→/g, '->')
    .replace(/←/g, '<-')
    .replace(/≥/g, '>=')
    .replace(/≤/g, '<=')
    .replace(/≠/g, '!=')
    .replace(/[—–]/g, '-')
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/…/g, '...')
    .replace(/•/g, '-')
    .replace(/[^\x00-\xFF]/g, '')

const riskText = (risk) =>
  risk?.riskRating
    ? `${risk.riskRating}`
    : 'Not scored'

const riskDetail = (risk) =>
  [risk?.likelihood, risk?.severity].filter(Boolean).join(' / ') || ''

const countBy = (rows, getKey, keys) => {
  const counts = Object.fromEntries(keys.map((key) => [key, 0]))
  let other = 0
  rows.forEach((row) => {
    const key = getKey(row)
    if (key in counts) counts[key] += 1
    else other += 1
  })
  return { counts, other }
}

const idNumber = (hazardID = '') => Number(String(hazardID).replace(/\D/g, '')) || 0

// ---------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------

const RatingCell = ({ rating, detail, flex = 1.2 }) => {
  const color = RISK_COLOR[rating]
  return (
    <View style={{ flex, paddingHorizontal: 3 }}>
      <View
        style={{
          backgroundColor: color || '#F1F5F9',
          borderRadius: 3,
          paddingVertical: 2,
          paddingHorizontal: 4,
          alignItems: 'center',
        }}
      >
        <Text style={{ fontSize: 7, color: color ? '#FFFFFF' : MUTED, fontFamily: 'Helvetica-Bold' }}>
          {rating || 'Not scored'}
        </Text>
      </View>
      {detail ? (
        <Text style={{ fontSize: 5.5, color: MUTED, textAlign: 'center', marginTop: 1 }}>
          {clean(detail)}
        </Text>
      ) : null}
    </View>
  )
}

const Pill = ({ label, color }) => (
  <View
    style={{
      backgroundColor: color || '#F1F5F9',
      borderRadius: 3,
      paddingVertical: 1.5,
      paddingHorizontal: 4,
      alignSelf: 'flex-start',
    }}
  >
    <Text style={{ fontSize: 6.5, color: color ? '#FFFFFF' : MUTED, fontFamily: 'Helvetica-Bold' }}>
      {clean(label)}
    </Text>
  </View>
)

const SectionTitle = ({ children, marginTop = 14 }) => (
  <Text
    style={{
      fontSize: 11,
      color: NAVY,
      fontFamily: 'Helvetica-Bold',
      marginTop,
      marginBottom: 4,
    }}
    minPresenceAhead={40}
  >
    {children}
  </Text>
)

const StatBox = ({ label, value, color }) => (
  <View
    style={{
      flex: 1,
      borderWidth: 0.5,
      borderColor: LINE,
      borderRadius: 4,
      padding: 8,
      marginRight: 6,
    }}
  >
    <Text style={{ fontSize: 6.5, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.5 }}>
      {label}
    </Text>
    <Text style={{ fontSize: 18, color: color || NAVY, fontFamily: 'Helvetica-Bold', marginTop: 2 }}>
      {value}
    </Text>
  </View>
)

const CountTable = ({ title, rows }) => (
  <View style={{ flex: 1, marginRight: 8 }}>
    <Text style={{ fontSize: 8, color: NAVY, fontFamily: 'Helvetica-Bold', marginBottom: 3 }}>{title}</Text>
    {rows.map(([label, value, color]) => (
      <View
        key={label}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingVertical: 2,
          borderBottomWidth: 0.5,
          borderBottomColor: LINE,
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          {color ? (
            <View style={{ width: 7, height: 7, borderRadius: 2, backgroundColor: color, marginRight: 4 }} />
          ) : null}
          <Text style={{ fontSize: 7.5 }}>{label}</Text>
        </View>
        <Text style={{ fontSize: 7.5, fontFamily: 'Helvetica-Bold' }}>{value}</Text>
      </View>
    ))}
  </View>
)

const RiskMatrix = () => (
  <View>
    <Text style={{ fontSize: 8, color: NAVY, fontFamily: 'Helvetica-Bold', marginBottom: 3 }}>
      Risk matrix (CRMP Appendix A)
    </Text>
    <View style={{ flexDirection: 'row' }}>
      <Text style={{ width: 62, fontSize: 5.5, color: MUTED, paddingTop: 4 }}>Likelihood v / Severity {'>'}</Text>
      {SEVERITIES.map((severity) => (
        <Text key={severity} style={{ width: 46, fontSize: 5.5, color: MUTED, textAlign: 'center', paddingVertical: 2 }}>
          {severity}
        </Text>
      ))}
    </View>
    {LIKELIHOODS.map((likelihood) => (
      <View key={likelihood} style={{ flexDirection: 'row', alignItems: 'center' }}>
        <Text style={{ width: 62, fontSize: 6, paddingRight: 3 }}>{likelihood}</Text>
        {MATRIX[likelihood].map((rating, index) => (
          <View
            key={`${likelihood}-${index}`}
            style={{
              width: 44,
              marginRight: 2,
              marginBottom: 2,
              backgroundColor: RISK_COLOR[rating],
              paddingVertical: 3,
              alignItems: 'center',
              borderRadius: 2,
            }}
          >
            <Text style={{ fontSize: 6, color: '#FFFFFF', fontFamily: 'Helvetica-Bold' }}>{rating}</Text>
          </View>
        ))}
      </View>
    ))}
    <Text style={{ fontSize: 6, color: MUTED, marginTop: 3 }}>
      Low: acceptable, record and monitor. Medium: acceptable if ALARP. High: not acceptable without a control plan. Critical: unacceptable until reduced or formally accepted by the CSO and a director.
    </Text>
  </View>
)

const Footer = ({ title }) => (
  <View
    style={{
      position: 'absolute',
      bottom: 16,
      left: 30,
      right: 30,
      flexDirection: 'row',
      justifyContent: 'space-between',
    }}
    fixed
  >
    <Text style={{ fontSize: 6.5, color: '#94A3B8' }}>
      AmbuNet - {title} - DCB0129 hazard log - CONFIDENTIAL
    </Text>
    <Text
      style={{ fontSize: 6.5, color: '#94A3B8' }}
      render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
    />
  </View>
)

const PAGE_STYLE = {
  paddingTop: 30,
  paddingBottom: 36,
  paddingHorizontal: 30,
  fontFamily: 'Helvetica',
  fontSize: 8,
  color: '#0F172A',
}

// ---------------------------------------------------------------------------
// Register table
// ---------------------------------------------------------------------------

const COLUMNS = [
  ['ID', 0.9],
  ['Hazard', 3.6],
  ['Module / Type', 1.5],
  ['Status', 0.9],
  ['Initial risk', 1.2],
  ['Residual risk', 1.2],
  ['Controls', 0.9],
  ['Acceptability', 1],
  ['Next review', 0.9],
]

const TableHeader = () => (
  <View
    style={{
      flexDirection: 'row',
      backgroundColor: NAVY,
      paddingVertical: 4,
      paddingHorizontal: 4,
      borderRadius: 3,
    }}
    fixed
  >
    {COLUMNS.map(([label, flex]) => (
      <Text
        key={label}
        style={{
          flex,
          paddingHorizontal: 3,
          fontSize: 6.5,
          color: '#FFFFFF',
          fontFamily: 'Helvetica-Bold',
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          textAlign: ['Initial risk', 'Residual risk', 'Controls'].includes(label) ? 'center' : 'left',
        }}
      >
        {label}
      </Text>
    ))}
  </View>
)

const RegisterRow = ({ hazard, striped }) => {
  const controls = hazard.controlMeasures || []
  const implemented = controls.filter((c) => c?.implemented).length
  const planned = controls.filter((c) => c?.type === 'planned' && !c?.implemented).length
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 4,
        paddingHorizontal: 4,
        backgroundColor: striped ? '#F8FAFC' : undefined,
        borderBottomWidth: 0.5,
        borderBottomColor: LINE,
      }}
      wrap={false}
    >
      <Text style={{ flex: 0.9, paddingHorizontal: 3, fontSize: 7, fontFamily: 'Helvetica-Bold' }}>
        {hazard.hazardID}
      </Text>
      <Text style={{ flex: 3.6, paddingHorizontal: 3, fontSize: 7 }}>{clean(hazard.title)}</Text>
      <View style={{ flex: 1.5, paddingHorizontal: 3 }}>
        <Text style={{ fontSize: 6.5 }}>{clean(hazard.module)}</Text>
        <Text style={{ fontSize: 6, color: MUTED }}>{clean(hazard.hazardType)}</Text>
      </View>
      <View style={{ flex: 0.9, paddingHorizontal: 3 }}>
        <Pill label={hazard.status || 'Unknown'} color={STATUS_COLOR[hazard.status]} />
      </View>
      <RatingCell rating={hazard.initialRisk?.riskRating} detail={riskDetail(hazard.initialRisk)} />
      <RatingCell rating={hazard.residualRisk?.riskRating} detail={riskDetail(hazard.residualRisk)} />
      <View style={{ flex: 0.9, paddingHorizontal: 3, alignItems: 'center' }}>
        <Text style={{ fontSize: 7 }}>
          {controls.length ? `${implemented} of ${controls.length}` : 'None'}
        </Text>
        {planned > 0 ? (
          <Text style={{ fontSize: 6, color: '#B54708' }}>{planned} planned</Text>
        ) : null}
      </View>
      <View style={{ flex: 1, paddingHorizontal: 3 }}>
        {hazard.riskAcceptability ? (
          <Pill label={hazard.riskAcceptability} color={ACCEPTABILITY_COLOR[hazard.riskAcceptability]} />
        ) : (
          <Text style={{ fontSize: 6.5, color: MUTED }}>Not decided</Text>
        )}
      </View>
      <Text style={{ flex: 0.9, paddingHorizontal: 3, fontSize: 6.5, color: MUTED }}>
        {hazard.nextReviewDate ? moment(hazard.nextReviewDate).format('D MMM YYYY') : '-'}
      </Text>
    </View>
  )
}

// ---------------------------------------------------------------------------
// Detail block
// ---------------------------------------------------------------------------

const Bullets = ({ items }) =>
  items.length ? (
    items.map((item, index) => (
      <Text key={index} style={{ fontSize: 6.5, marginLeft: 6, marginBottom: 1 }}>
        - {clean(item)}
      </Text>
    ))
  ) : (
    <Text style={{ fontSize: 6.5, color: MUTED, marginLeft: 6 }}>None recorded</Text>
  )

const Label = ({ children }) => (
  <Text
    style={{
      fontSize: 6,
      color: MUTED,
      textTransform: 'uppercase',
      letterSpacing: 0.4,
      marginTop: 4,
      marginBottom: 1,
      fontFamily: 'Helvetica-Bold',
    }}
  >
    {children}
  </Text>
)

const HazardDetail = ({ hazard }) => {
  const controls = hazard.controlMeasures || []
  const signOff = hazard.clinicalSafetyOfficerSignOff || {}
  const signOffName = [signOff.signedOffBy?.firstName, signOff.signedOffBy?.lastName]
    .filter(Boolean)
    .join(' ')
  return (
    // Blocks may split across pages: Codebase entries carry long file:line
    // evidence in Notes and a non-wrapping block taller than a page would be
    // clipped. The header stays with at least the first few lines.
    <View
      style={{
        borderWidth: 0.5,
        borderColor: LINE,
        borderRadius: 4,
        marginBottom: 8,
      }}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          backgroundColor: '#F1F5F9',
          paddingVertical: 4,
          paddingHorizontal: 8,
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
        }}
        wrap={false}
        minPresenceAhead={80}
      >
        <Text style={{ fontSize: 8, fontFamily: 'Helvetica-Bold', color: NAVY, marginRight: 8 }}>
          {hazard.hazardID}
        </Text>
        <Text style={{ fontSize: 8, fontFamily: 'Helvetica-Bold', flex: 1 }}>{clean(hazard.title)}</Text>
        <View style={{ marginLeft: 6 }}>
          <Pill label={hazard.status || 'Unknown'} color={STATUS_COLOR[hazard.status]} />
        </View>
        <View style={{ marginLeft: 6 }}>
          <Pill label={`Initial: ${riskText(hazard.initialRisk)}`} color={RISK_COLOR[hazard.initialRisk?.riskRating]} />
        </View>
        <View style={{ marginLeft: 6 }}>
          <Pill label={`Residual: ${riskText(hazard.residualRisk)}`} color={RISK_COLOR[hazard.residualRisk?.riskRating]} />
        </View>
      </View>

      <View style={{ flexDirection: 'row', paddingHorizontal: 8, paddingVertical: 6 }}>
        <View style={{ flex: 1.4, paddingRight: 10 }}>
          <Text style={{ fontSize: 6.5, color: MUTED, marginBottom: 3 }}>
            {[hazard.module, hazard.hazardType, hazard.source ? `Source: ${hazard.source}` : null, hazard.dateIdentified ? `Identified ${moment(hazard.dateIdentified).format('D MMM YYYY')}` : null]
              .filter(Boolean)
              .map(clean)
              .join('  |  ')}
          </Text>
          <Text style={{ fontSize: 7, lineHeight: 1.35 }}>{clean(hazard.description)}</Text>
          <Label>Causes</Label>
          <Bullets items={hazard.cause || []} />
          <Label>Clinical impact</Label>
          <Bullets items={hazard.clinicalImpact || []} />
          {hazard.notes ? (
            <>
              <Label>Notes and evidence</Label>
              <Text style={{ fontSize: 6.5, color: '#334155', lineHeight: 1.3 }}>{clean(hazard.notes)}</Text>
            </>
          ) : null}
        </View>

        <View style={{ flex: 1, borderLeftWidth: 0.5, borderLeftColor: LINE, paddingLeft: 10 }}>
          <Label>Risk scoring</Label>
          <View style={{ flexDirection: 'row', marginBottom: 2 }}>
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: 6.5 }}>Initial: {riskDetail(hazard.initialRisk) || 'not scored'}</Text>
            </View>
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: 6.5 }}>Residual: {riskDetail(hazard.residualRisk) || 'not scored'}</Text>
            </View>
          </View>
          <Text style={{ fontSize: 6.5 }}>
            Acceptability: {hazard.riskAcceptability || 'not decided'}
            {'   '}CSO sign-off: {signOff.signedOff ? `yes${signOff.signedOffDate ? ` (${moment(signOff.signedOffDate).format('D MMM YYYY')}${signOffName ? `, ${signOffName}` : ''})` : ''}` : 'no'}
          </Text>
          <Label>Controls ({controls.filter((c) => c.implemented).length} of {controls.length} in place)</Label>
          {controls.length ? (
            controls.map((control, index) => (
              <View key={index} style={{ flexDirection: 'row', marginBottom: 2 }}>
                <View
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 3,
                    marginTop: 1.5,
                    marginRight: 4,
                    backgroundColor: control.implemented ? '#27803B' : control.type === 'planned' ? '#B54708' : '#94A3B8',
                  }}
                />
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 6.5, fontFamily: 'Helvetica-Bold' }}>
                    {clean(control.control || 'Control')}{' '}
                    <Text style={{ fontFamily: 'Helvetica', color: MUTED }}>
                      ({control.type === 'planned' ? 'planned' : 'existing'}, {control.implemented ? 'implemented' : 'not yet implemented'})
                    </Text>
                  </Text>
                  {control.description ? (
                    <Text style={{ fontSize: 6.5, color: '#334155', lineHeight: 1.3 }}>{clean(control.description)}</Text>
                  ) : null}
                </View>
              </View>
            ))
          ) : (
            <Text style={{ fontSize: 6.5, color: MUTED }}>None recorded</Text>
          )}
          {(hazard.relatedHazards?.length || hazard.linkedFeatures?.length) ? (
            <>
              <Label>Links</Label>
              <Text style={{ fontSize: 6.5, color: '#334155' }}>
                {[
                  hazard.relatedHazards?.length ? `Related: ${hazard.relatedHazards.join(', ')}` : null,
                  hazard.linkedFeatures?.length ? `Features: ${clean(hazard.linkedFeatures.join(', '))}` : null,
                ]
                  .filter(Boolean)
                  .join('   ')}
              </Text>
            </>
          ) : null}
          {hazard.nextReviewDate ? (
            <Text style={{ fontSize: 6.5, color: MUTED, marginTop: 3 }}>
              Next review {moment(hazard.nextReviewDate).format('D MMM YYYY')}
            </Text>
          ) : null}
        </View>
      </View>
    </View>
  )
}

// ---------------------------------------------------------------------------
// Document
// ---------------------------------------------------------------------------

const HazardLogPDF = ({ register = 'Clinical', hazards = [], downloadedBy }) => {
  const meta = REGISTERS[register] || REGISTERS.Clinical
  const title = meta.label
  const now = moment()
  const sorted = [...hazards].sort((a, b) => idNumber(a.hazardID) - idNumber(b.hazardID))

  const status = countBy(sorted, (h) => h.status, ['Open', 'In Progress', 'Mitigated', 'Accepted'])
  const initial = countBy(sorted, (h) => h.initialRisk?.riskRating, ['Critical', 'High', 'Medium', 'Low'])
  const residual = countBy(sorted, (h) => h.residualRisk?.riskRating, ['Critical', 'High', 'Medium', 'Low'])
  const acceptability = countBy(sorted, (h) => h.riskAcceptability, ['Acceptable', 'ALARP', 'Unacceptable'])

  const openCount = status.counts.Open + status.counts['In Progress']
  const highInitial = initial.counts.Critical + initial.counts.High
  const outstanding = sorted.filter((h) =>
    (h.controlMeasures || []).some((c) => c?.type === 'planned' && !c?.implemented),
  ).length
  const signedOff = sorted.filter((h) => h.clinicalSafetyOfficerSignOff?.signedOff).length
  const numbers = sorted.map((h) => idNumber(h.hazardID)).filter(Boolean)
  const idRange = numbers.length
    ? `${sorted.find((h) => idNumber(h.hazardID) === Math.min(...numbers))?.hazardID} to ${sorted.find((h) => idNumber(h.hazardID) === Math.max(...numbers))?.hazardID}`
    : 'None'

  const byModule = {}
  sorted.forEach((h) => {
    const key = h.module || 'Unspecified'
    byModule[key] = (byModule[key] || 0) + 1
  })
  const moduleRows = Object.entries(byModule)
    .sort((a, b) => b[1] - a[1])
    .map(([label, value]) => [label, value])

  return (
    <Document
      title={`${title} - ${now.format('YYYY-MM-DD')}`}
      author="AmbuNet"
      subject="DCB0129 hazard log export"
      options={{ isEvalSupported: false }}
    >
      {/* Cover */}
      <Page size="A4" orientation="landscape" style={PAGE_STYLE}>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <Image src={AmbuNetLogo} style={{ width: 44, height: 44, marginRight: 10 }} />
            <View>
              <Text style={{ fontSize: 16, fontFamily: 'Helvetica-Bold', color: NAVY }}>AmbuNet</Text>
              <Text style={{ fontSize: 7, color: MUTED, letterSpacing: 1, textTransform: 'uppercase' }}>
                Clinical risk management
              </Text>
            </View>
          </View>
          <View style={{ alignItems: 'flex-end' }}>
            <Text style={{ fontSize: 7, color: MUTED }}>Downloaded {now.format('dddd D MMMM YYYY, HH:mm')}</Text>
            {downloadedBy ? <Text style={{ fontSize: 7, color: MUTED }}>by {clean(downloadedBy)}</Text> : null}
          </View>
        </View>

        <View
          style={{
            marginTop: 22,
            paddingVertical: 18,
            paddingHorizontal: 20,
            borderRadius: 6,
            backgroundColor: NAVY,
          }}
        >
          <Text style={{ fontSize: 26, color: '#FFFFFF', fontFamily: 'Helvetica-Bold' }}>{title}</Text>
          <Text style={{ fontSize: 9, color: '#CFE8EB', marginTop: 4 }}>
            DCB0129 Clinical Risk Management: its Application in the Manufacture of Health IT Systems - Hazard Log, {meta.short} register
          </Text>
          <Text style={{ fontSize: 7.5, color: '#CFE8EB', marginTop: 6, lineHeight: 1.4, maxWidth: 640 }}>
            {clean(meta.subtitle)}
          </Text>
        </View>

        <SectionTitle>Summary as at {now.format('D MMMM YYYY')}</SectionTitle>
        <View style={{ flexDirection: 'row' }}>
          <StatBox label="Hazards logged" value={sorted.length} />
          <StatBox label="ID range" value={idRange} />
          <StatBox label="Open or in progress" value={openCount} color={openCount ? '#B54708' : '#27803B'} />
          <StatBox label="High or critical (initial)" value={highInitial} color={highInitial ? '#B42318' : '#27803B'} />
          <StatBox label="Controls outstanding" value={outstanding} color={outstanding ? '#5B48B0' : '#27803B'} />
          <StatBox label="CSO signed off" value={`${signedOff} of ${sorted.length}`} color={TEAL} />
        </View>

        <View style={{ flexDirection: 'row', marginTop: 14 }}>
          <CountTable
            title="By status"
            rows={[
              ['Open', status.counts.Open, STATUS_COLOR.Open],
              ['In Progress', status.counts['In Progress'], STATUS_COLOR['In Progress']],
              ['Mitigated', status.counts.Mitigated, STATUS_COLOR.Mitigated],
              ['Accepted', status.counts.Accepted, STATUS_COLOR.Accepted],
              ...(status.other ? [['Other', status.other]] : []),
            ]}
          />
          <CountTable
            title="By initial risk"
            rows={[
              ['Critical', initial.counts.Critical, RISK_COLOR.Critical],
              ['High', initial.counts.High, RISK_COLOR.High],
              ['Medium', initial.counts.Medium, RISK_COLOR.Medium],
              ['Low', initial.counts.Low, RISK_COLOR.Low],
              ['Not scored', initial.other],
            ]}
          />
          <CountTable
            title="By residual risk"
            rows={[
              ['Critical', residual.counts.Critical, RISK_COLOR.Critical],
              ['High', residual.counts.High, RISK_COLOR.High],
              ['Medium', residual.counts.Medium, RISK_COLOR.Medium],
              ['Low', residual.counts.Low, RISK_COLOR.Low],
              ['Not scored', residual.other],
            ]}
          />
          <CountTable
            title="By acceptability"
            rows={[
              ['Acceptable', acceptability.counts.Acceptable, ACCEPTABILITY_COLOR.Acceptable],
              ['ALARP', acceptability.counts.ALARP, ACCEPTABILITY_COLOR.ALARP],
              ['Unacceptable', acceptability.counts.Unacceptable, ACCEPTABILITY_COLOR.Unacceptable],
              ['Not decided', acceptability.other],
            ]}
          />
          <CountTable title="By module" rows={moduleRows.slice(0, 11)} />
        </View>

        <View style={{ marginTop: 14 }}>
          <RiskMatrix />
        </View>

        <Footer title={title} />
      </Page>

      {/* Register table */}
      <Page size="A4" orientation="landscape" style={PAGE_STYLE}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }} fixed>
          <Text style={{ fontSize: 11, fontFamily: 'Helvetica-Bold', color: NAVY }}>{title} - register</Text>
          <Text style={{ fontSize: 7, color: MUTED }}>
            {sorted.length} hazards, downloaded {now.format('D MMM YYYY HH:mm')}
          </Text>
        </View>
        <TableHeader />
        {sorted.length === 0 ? (
          <Text style={{ fontSize: 8, color: MUTED, padding: 8 }}>No hazards in this register.</Text>
        ) : (
          sorted.map((hazard, index) => (
            <RegisterRow key={hazard._id || hazard.hazardID} hazard={hazard} striped={index % 2 === 1} />
          ))
        )}
        <Footer title={title} />
      </Page>

      {/* Detail */}
      {sorted.length > 0 && (
        <Page size="A4" orientation="landscape" style={PAGE_STYLE}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }} fixed>
            <Text style={{ fontSize: 11, fontFamily: 'Helvetica-Bold', color: NAVY }}>{title} - hazard detail</Text>
            <Text style={{ fontSize: 7, color: MUTED }}>Causes, clinical impact, controls and scoring per hazard</Text>
          </View>
          {sorted.map((hazard) => (
            <HazardDetail key={hazard._id || hazard.hazardID} hazard={hazard} />
          ))}
          <Footer title={title} />
        </Page>
      )}
    </Document>
  )
}

export default HazardLogPDF
